/**
 * BillingPricingGrid Component
 * 
 * Displays all available plans in a grid format for the billing settings page.
 * Shows current plan highlighted, with upgrade/downgrade options.
 * Handles blockers for plan transitions.
 */

import React, { useState, useEffect } from 'react';
import { Check, X, Crown, Sparkles, Clock, AlertTriangle, Loader2, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { billingApi, stripeApi } from '../../services/api';
import { toast } from 'sonner';

const BillingPricingGrid = ({ onPlanChange }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPlanId, setCurrentPlanId] = useState(null);
  const [canManageBilling, setCanManageBilling] = useState(false);
  const [trialInfo, setTrialInfo] = useState(null);
  const [processingPlanId, setProcessingPlanId] = useState(null);
  const [showBlockerModal, setShowBlockerModal] = useState(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await billingApi.getAvailablePlans();
      const data = response.data || response;
      setPlans(data.plans || []);
      setCurrentPlanId(data.current_plan_id);
      setCanManageBilling(data.can_manage_billing);
      setTrialInfo(data.trial_info);
    } catch (error) {
      console.error('[BillingPricingGrid] Error fetching plans:', error);
      console.error('[BillingPricingGrid] Error source: fetchPlans catch block');
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const handlePlanSelect = async (plan) => {
    if (!canManageBilling) {
      toast.error('Only owners and billing admins can change plans');
      return;
    }

    if (plan.is_current) return;

    // Check for blockers
    if (plan.has_blockers) {
      setShowBlockerModal(plan);
      return;
    }

    setProcessingPlanId(plan.plan_id);

    try {
      if (plan.plan_type === 'inquire') {
        // Contact sales - open email or form
        window.location.href = 'mailto:sales@stackranked.ai?subject=Enterprise Plan Inquiry';
        setProcessingPlanId(null);
        return;
      }

      if (plan.relationship === 'upgrade') {
        // Check if free -> paid (needs checkout) or paid -> paid (one-click)
        const currentPlan = plans.find(p => p.is_current);
        
        if (currentPlan?.plan_type === 'free') {
          // Free to paid - need Stripe checkout
          const checkoutResponse = await stripeApi.createCheckoutSession({
            plan_id: plan.plan_id,
            success_url: `${window.location.origin}/settings?tab=billing&upgrade=success`,
            cancel_url: `${window.location.origin}/settings?tab=billing&upgrade=cancelled`
          });
          
          if (checkoutResponse.data?.checkout_url) {
            // Show trial info before redirecting if applicable
            if (checkoutResponse.data?.trial_preserved) {
              toast.info('Your trial will be preserved after checkout', { duration: 3000 });
            }
            window.location.href = checkoutResponse.data.checkout_url;
          } else if (checkoutResponse.checkout_url) {
            window.location.href = checkoutResponse.checkout_url;
          }
        } else {
          // Paid to different paid - one-click upgrade
          const response = await billingApi.oneClickUpgrade(plan.plan_id);
          
          // Show appropriate toast based on trial status
          if (response.data?.trial_preserved) {
            toast.success(`Successfully upgraded to ${plan.label}`, {
              description: '🎉 Your trial period has been preserved!',
              duration: 5000
            });
          } else {
            toast.success(`Successfully upgraded to ${plan.label}`);
          }
          fetchPlans();
          onPlanChange?.();
        }
      } else if (plan.relationship === 'downgrade') {
        // Downgrade flow
        if (plan.plan_type === 'free') {
          // Downgrade to free - cancel subscription
          const confirmed = window.confirm(
            `Are you sure you want to downgrade to the Free plan? This will cancel your subscription at the end of the billing period.`
          );
          
          if (confirmed) {
            await billingApi.cancelSubscription();
            toast.success('Subscription cancelled. You will be downgraded at the end of your billing period.');
            fetchPlans();
            onPlanChange?.();
          }
        } else {
          // Downgrade to lower paid tier
          const response = await billingApi.oneClickUpgrade(plan.plan_id);
          
          // Show appropriate toast based on trial status
          if (response.data?.trial_preserved) {
            toast.success(`Plan changed to ${plan.label}`, {
              description: '🎉 Your trial period has been preserved!',
              duration: 5000
            });
          } else {
            toast.success(`Plan changed to ${plan.label}`);
          }
          fetchPlans();
          onPlanChange?.();
        }
      }
    } catch (error) {
      console.error('[BillingPricingGrid] Error changing plan:', error);
      console.error('[BillingPricingGrid] Error source: handlePlanSelect catch block');
      toast.error(error.response?.data?.detail || 'Failed to change plan');
    } finally {
      setProcessingPlanId(null);
    }
  };

  if (loading) {
    return (
      <Card className={cn("p-8", colors.bgCard, colors.borderPrimary)}>
        <div className="flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>
          Choose Your Plan
        </h3>
        {trialInfo && (
          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-700">
            <Clock className="w-3 h-3 mr-1" />
            {trialInfo.days_remaining} days left in trial
          </Badge>
        )}
      </div>

      {/* Plans Grid */}
      <div className={cn(
        "grid gap-4",
        plans.length === 2 ? "grid-cols-1 md:grid-cols-2" :
        plans.length === 3 ? "grid-cols-1 md:grid-cols-3" :
        "grid-cols-1 md:grid-cols-2 lg:grid-cols-4"
      )}>
        {plans.map((plan) => (
          <PlanCard
            key={plan.plan_id}
            plan={plan}
            isProcessing={processingPlanId === plan.plan_id}
            canManageBilling={canManageBilling}
            onSelect={() => handlePlanSelect(plan)}
            colors={colors}
          />
        ))}
      </div>

      {/* Blocker Modal */}
      {showBlockerModal && (
        <BlockerModal
          plan={showBlockerModal}
          onClose={() => setShowBlockerModal(null)}
          colors={colors}
        />
      )}

      {/* Permission notice for non-billing users */}
      {!canManageBilling && (
        <p className={cn("text-sm text-center", colors.textMuted)}>
          Contact your organization owner to change plans.
        </p>
      )}
    </div>
  );
};

/**
 * Individual Plan Card
 */
const PlanCard = ({ plan, isProcessing, canManageBilling, onSelect, colors }) => {
  const isCurrent = plan.is_current;
  const isPopular = plan.highlight && !isCurrent;
  const isInquire = plan.plan_type === 'inquire';
  
  return (
    <Card
      className={cn(
        "relative flex flex-col p-5 transition-all duration-200",
        colors.bgCard,
        isCurrent 
          ? "border-2 border-blue-500 dark:border-blue-400 ring-2 ring-blue-100 dark:ring-blue-900/30" 
          : isPopular
            ? "border-2 border-purple-400 dark:border-purple-500"
            : colors.borderPrimary,
        !isCurrent && canManageBilling && !plan.has_blockers && "hover:border-blue-300 dark:hover:border-blue-600 cursor-pointer"
      )}
      onClick={() => !isCurrent && canManageBilling && onSelect()}
    >
      {/* Current Plan Badge */}
      {isCurrent && (
        <div className="absolute -top-3 left-4">
          <Badge className="bg-blue-600 text-white hover:bg-blue-600">
            <Crown className="w-3 h-3 mr-1" />
            Current Plan
          </Badge>
        </div>
      )}

      {/* Popular Badge */}
      {isPopular && !isCurrent && (
        <div className="absolute -top-3 left-4">
          <Badge className="bg-purple-600 text-white hover:bg-purple-600">
            <Sparkles className="w-3 h-3 mr-1" />
            Most Popular
          </Badge>
        </div>
      )}

      {/* Trial Badge */}
      {isCurrent && plan.trial_info && (
        <div className="absolute -top-3 right-4">
          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-400">
            <Clock className="w-3 h-3 mr-1" />
            {plan.trial_info.days_remaining}d trial
          </Badge>
        </div>
      )}

      {/* Plan Name & Price */}
      <div className={cn("mt-2", (isCurrent || isPopular) && "mt-4")}>
        <h4 className={cn("text-lg font-bold", colors.textPrimary)}>
          {plan.label}
        </h4>
        <div className="mt-2 flex items-baseline gap-1">
          {isInquire ? (
            <span className={cn("text-2xl font-bold", colors.textPrimary)}>Custom</span>
          ) : (
            <>
              <span className={cn("text-3xl font-bold", colors.textPrimary)}>
                {plan.price_display}
              </span>
              {plan.price_cents > 0 && (
                <span className={cn("text-sm", colors.textMuted)}>
                  /{plan.billing_period}
                </span>
              )}
            </>
          )}
        </div>
      </div>

      {/* Features List */}
      <ul className="mt-4 space-y-2 flex-1">
        {plan.feature_bullets.map((feature, idx) => (
          <li key={idx} className="flex items-start gap-2">
            {feature.included ? (
              <Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
            ) : (
              <X className="w-4 h-4 text-gray-300 mt-0.5 shrink-0" />
            )}
            <span className={cn(
              "text-sm",
              feature.included ? colors.textPrimary : colors.textMuted
            )}>
              {feature.text}
            </span>
          </li>
        ))}
      </ul>

      {/* Blockers Warning */}
      {plan.has_blockers && !isCurrent && (
        <div className="mt-3 p-2 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
          <p className="text-xs text-amber-700 dark:text-amber-400 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            {plan.blockers.length} blocker{plan.blockers.length !== 1 ? 's' : ''} to resolve
          </p>
        </div>
      )}

      {/* CTA Button */}
      <div className="mt-4">
        <Button
          className={cn(
            "w-full",
            isCurrent 
              ? "bg-gray-100 text-gray-500 cursor-default hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-400"
              : plan.relationship === 'upgrade'
                ? "bg-blue-600 hover:bg-blue-700 text-white"
                : plan.relationship === 'downgrade'
                  ? "bg-gray-600 hover:bg-gray-700 text-white"
                  : isInquire
                    ? "bg-purple-600 hover:bg-purple-700 text-white"
                    : "bg-blue-600 hover:bg-blue-700 text-white"
          )}
          disabled={isCurrent || isProcessing || (!canManageBilling && !isInquire)}
          onClick={(e) => {
            e.stopPropagation();
            if (!isCurrent) onSelect();
          }}
        >
          {isProcessing ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Processing...
            </>
          ) : isInquire && !isCurrent ? (
            <>
              {plan.cta_text}
              <ExternalLink className="w-4 h-4 ml-2" />
            </>
          ) : (
            plan.cta_text
          )}
        </Button>
      </div>
    </Card>
  );
};

/**
 * Blocker Resolution Modal
 */
const BlockerModal = ({ plan, onClose, colors }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <Card className={cn("w-full max-w-md p-6 m-4", colors.bgCard)}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="flex-1">
            <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>
              Cannot {plan.relationship === 'downgrade' ? 'Downgrade' : 'Change'} Plan
            </h3>
            <p className={cn("text-sm mt-1", colors.textSecondary)}>
              You need to resolve the following before switching to {plan.label}:
            </p>
          </div>
        </div>

        <div className="mt-4 space-y-3">
          {plan.blockers.map((blocker, idx) => (
            <div 
              key={idx}
              className={cn(
                "p-3 rounded-lg border",
                colors.bgTertiary,
                colors.borderPrimary
              )}
            >
              <div className="flex items-center justify-between">
                <span className={cn("text-sm font-medium capitalize", colors.textPrimary)}>
                  {blocker.resource}
                </span>
                <Badge variant="outline" className="text-red-600 border-red-300">
                  {blocker.current} / {blocker.limit}
                </Badge>
              </div>
              <p className={cn("text-xs mt-1", colors.textMuted)}>
                {blocker.action}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-6 flex gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={onClose}
          >
            Got it
          </Button>
          {plan.blockers.some(b => b.resource === 'products') && (
            <Button
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => {
                onClose();
                window.location.href = '/products';
              }}
            >
              Manage Products
            </Button>
          )}
          {plan.blockers.some(b => b.resource === 'seats') && (
            <Button
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => {
                onClose();
                window.location.href = '/settings?tab=team';
              }}
            >
              Manage Team
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};

export default BillingPricingGrid;
